/**
 * FleetModule Component
 * Vehicle and equipment management
 */
import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FleetModule = ({ onBack, currentUser }) => {
  const [equipment, setEquipment] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [filterType, setFilterType] = useState('All');
  const [showFleetFiles, setShowFleetFiles] = useState(false);
  const [fleetFilesEquipment, setFleetFilesEquipment] = useState(null);
  const [fleetFiles, setFleetFiles] = useState([]);
  const [fleetFilesUploading, setFleetFilesUploading] = useState(false);
  const [fleetFileForm, setFleetFileForm] = useState({
    filename: '',
    file_type: '',
    file_data: '',
    uploaded_by: ''
  });

  const [formData, setFormData] = useState({
    equipment_type: 'Truck',
    unit_number: '',
    make: '',
    model: '',
    year: '',
    vin: '',
    license_plate: '',
    description: '',
    status: 'Active',
    last_cvor: '',
    next_cvor: '',
    repairs_needed: false,
    notes: ''
  });

  const equipmentTypes = ['Truck', 'Trailer', 'Equipment'];
  const statusOptions = ['Active', 'Maintenance', 'Retired'];

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API}/fleet`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Error fetching fleet:', error);
      // Initialize default fleet if none exists
      try {
        await axios.post(`${API}/fleet/initialize`);
        const response = await axios.get(`${API}/fleet`);
        setEquipment(response.data);
      } catch (initError) {
        console.error('Error initializing fleet:', initError);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚛 Fleet handleSubmit called');
    console.log('📝 Form data:', formData);
    console.log('✏️ Editing equipment:', editingEquipment);
    console.log('🔗 API URL:', API);
    
    try {
      if (editingEquipment) {
        console.log('🔄 Updating equipment with ID:', editingEquipment.id);
        console.log('🌐 PUT request to:', `${API}/fleet/${editingEquipment.id}`);
        
        const response = await axios.put(`${API}/fleet/${editingEquipment.id}`, formData);
        console.log('✅ Update response:', response);
        alert('Equipment updated successfully!');
      } else {
        console.log('➕ Creating new equipment');
        console.log('🌐 POST request to:', `${API}/fleet`);
        
        const response = await axios.post(`${API}/fleet`, formData);
        console.log('✅ Create response:', response);
        alert('Equipment added successfully!');
      }
      
      // Reset form
      setFormData({
        equipment_type: 'Truck',
        unit_number: '',
        make: '',
        model: '',
        year: '',
        vin: '',
        license_plate: '',
        description: '',
        status: 'Active',
        last_cvor: '',
        next_cvor: '',
        repairs_needed: false,
        notes: ''
      });
      setShowAddForm(false);
      setEditingEquipment(null);
      fetchEquipment();
    } catch (error) {
      console.error('❌ Fleet error details:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      
      if (error.response?.status === 400) {
        alert('Unit number already exists. Please use a different unit number.');
      } else if (error.response?.status === 401) {
        alert('❌ Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        alert('❌ Access denied. You may not have permission to edit fleet items.');
      } else if (error.response?.status === 404) {
        alert('❌ Fleet item not found. It may have been deleted.');
      } else if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        const message = Array.isArray(detail)
          ? detail.map((item) => item.msg).join(', ')
          : 'Please check required fields and try again.';
        alert(`❌ Validation error: ${message}`);
      } else if (error.response?.status === 500) {
        alert(`❌ Server error: ${error.response?.data?.detail || 'Internal server error'}`);
      } else {
        alert(`❌ Error editing equipment: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingEquipment(item);
    setFormData({
      equipment_type: item.equipment_type || 'Truck',
      unit_number: item.unit_number || '',
      make: item.make || '',
      model: item.model || '',
      year: item.year || '',
      vin: item.vin || '',
      license_plate: item.license_plate || '',
      description: item.description || '',
      status: item.status || 'Active',
      last_cvor: item.last_cvor || '',
      next_cvor: item.next_cvor || '',
      repairs_needed: !!item.repairs_needed,
      notes: item.notes || ''
    });
    setShowAddForm(true);
  };

  const openFleetFiles = (item) => {
    setFleetFilesEquipment(item);
    setShowFleetFiles(true);
    fetchFleetFiles(item.id);
  };

  const fetchFleetFiles = async (equipmentId) => {
    const targetId = equipmentId || fleetFilesEquipment?.id;
    if (!targetId) return;
    try {
      const response = await axios.get(`${API}/fleet/${targetId}/files`);
      setFleetFiles(response.data);
    } catch (error) {
      console.error('Error fetching fleet files:', error);
      setFleetFiles([]);
    }
  };

  const handleFleetFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a PDF or Word document.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Data = e.target.result.split(',')[1];
      setFleetFileForm(prev => ({
        ...prev,
        filename: file.name,
        file_type: file.type,
        file_data: base64Data
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleFleetFileUpload = async (event) => {
    event.preventDefault();
    if (!fleetFilesEquipment?.id) return;
    if (!fleetFileForm.file_data || !fleetFileForm.uploaded_by) {
      alert('Please select a file and enter your name.');
      return;
    }
    setFleetFilesUploading(true);
    try {
      await axios.post(`${API}/fleet/${fleetFilesEquipment.id}/files`, fleetFileForm);
      setFleetFileForm({ filename: '', file_type: '', file_data: '', uploaded_by: '' });
      fetchFleetFiles(fleetFilesEquipment.id);
      alert('Fleet file uploaded successfully!');
    } catch (error) {
      console.error('Error uploading fleet file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setFleetFilesUploading(false);
    }
  };

  const handleFleetFileDelete = async (fileId) => {
    if (!fleetFilesEquipment?.id) return;
    if (!confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/fleet/${fleetFilesEquipment.id}/files/${fileId}`);
      fetchFleetFiles(fleetFilesEquipment.id);
    } catch (error) {
      console.error('Error deleting fleet file:', error);
      alert('Error deleting file. Please try again.');
    }
  };

  const viewFleetFile = (doc) => {
    if (!doc.file_data) {
      alert('No document data available.');
      return;
    }
    const byteCharacters = atob(doc.file_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: doc.file_type || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const downloadFleetFile = (doc) => {
    if (!doc.file_data) {
      alert('No document data available.');
      return;
    }
    const byteCharacters = atob(doc.file_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: doc.file_type || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDelete = async (item) => {
    if (confirm(`Are you sure you want to delete ${item.unit_number}?`)) {
      try {
        await axios.delete(`${API}/fleet/${item.id}`);
        alert('Equipment deleted successfully!');
        fetchEquipment();
      } catch (error) {
        console.error('Error deleting equipment:', error);
        alert('Error deleting equipment. Please try again.');
      }
    }
  };

  const filteredEquipment = filterType === 'All' 
    ? equipment 
    : equipment.filter(item => item.equipment_type === filterType);

  const getEquipmentIcon = (type) => {
    switch (type) {
      case 'Truck': return '🚛';
      case 'Trailer': return '🚚';
      case 'Equipment': return '🚜';
      default: return '🔧';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-green-400';
      case 'Maintenance': return 'text-yellow-400';
      case 'Retired': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getCVORStatusColor = (cvorDate) => {
    if (!cvorDate) return 'text-gray-400';
    
    const today = new Date();
    const cvorDueDate = new Date(cvorDate);
    const daysUntilDue = Math.ceil((cvorDueDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) return 'text-red-400'; // Expired
    if (daysUntilDue <= 30) return 'text-yellow-400'; // Due soon
    return 'text-green-400'; // Current
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <div className="container mx-auto px-4 py-8 pb-28">
        
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <button
            onClick={onBack}
            className="text-blue-600 hover:text-blue-700 text-base sm:text-2xl"
            data-testid="fleet-back-button"
          >
            ← Back
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">FLEET MANAGEMENT</h1>
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm sm:text-base"
              data-testid="fleet-add-equipment-button"
            >
              + Add Equipment
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['All', ...equipmentTypes].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded-xl font-bold text-sm sm:text-base transition-colors ${
                filterType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
              }`}
              data-testid={`fleet-filter-${type.toLowerCase()}`}
            >
              {type} {type !== 'All' && `(${equipment.filter(item => item.equipment_type === type).length})`}
            </button>
          ))}
        </div>

        {/* Add/Edit Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 overflow-y-auto" data-testid="fleet-form-modal">
            <div className="bg-white rounded-xl p-6 sm:p-8 max-w-2xl w-full mx-4 my-6 sm:my-0 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-gray-800" data-testid="fleet-form-title">
                {editingEquipment ? 'Edit Equipment' : 'Add New Equipment'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <select
                    value={formData.equipment_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, equipment_type: e.target.value }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-equipment-type"
                  >
                    {equipmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Unit Number (e.g., T001, TR001, E001)"
                    value={formData.unit_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit_number: e.target.value.toUpperCase() }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-unit-number"
                  />
                  <input
                    type="text"
                    placeholder="Make"
                    value={formData.make}
                    onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-make"
                  />
                  <input
                    type="text"
                    placeholder="Model"
                    value={formData.model}
                    onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-model"
                  />
                  <input
                    type="text"
                    placeholder="Year"
                    value={formData.year}
                    onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-year"
                  />
                  <input
                    type="text"
                    placeholder="VIN"
                    value={formData.vin}
                    onChange={(e) => setFormData(prev => ({ ...prev, vin: e.target.value.toUpperCase() }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-vin"
                  />
                  <input
                    type="text"
                    placeholder="License Plate (optional)"
                    value={formData.license_plate}
                    onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value.toUpperCase() }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="fleet-form-license-plate"
                  />
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="fleet-form-status"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                
                {/* CVOR Compliance Section */}
                <div className="border-t border-gray-300 pt-4">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">🚛 CVOR Compliance & Maintenance</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-2">Last CVOR Date</label>
                      <input
                        type="date"
                        value={formData.last_cvor}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_cvor: e.target.value }))}
                        className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="fleet-form-last-cvor"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-2">Safety Inspection Due Date</label>
                      <input
                        type="date"
                        value={formData.next_cvor}
                        onChange={(e) => setFormData(prev => ({ ...prev, next_cvor: e.target.value }))}
                        className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="fleet-form-next-cvor"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="flex items-center gap-3 text-gray-800">
                      <input
                        type="checkbox"
                        checked={formData.repairs_needed}
                        onChange={(e) => setFormData(prev => ({ ...prev, repairs_needed: e.target.checked }))}
                        className="w-5 h-5 rounded border-gray-300 focus:ring-blue-500"
                        data-testid="fleet-form-repairs-needed"
                      />
                      <span className="text-lg">🔧 Repairs Needed</span>
                    </label>
                  </div>
                </div>

                <textarea
                  placeholder="Notes (optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                  data-testid="fleet-form-notes"
                />
                <textarea
                  placeholder="Description (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                  data-testid="fleet-form-description"
                />
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold"
                    data-testid="fleet-form-submit"
                  >
                    {editingEquipment ? 'Update Equipment' : 'Add Equipment'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingEquipment(null);
                      setFormData({
                        equipment_type: 'Truck',
                        unit_number: '',
                        make: '',
                        model: '',
                        year: '',
                        vin: '',
                        license_plate: '',
                        description: '',
                        status: 'Active',
                        last_cvor: '',
                        next_cvor: '',
                        repairs_needed: false,
                        notes: ''
                      });
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
                    data-testid="fleet-form-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Equipment List */}
        <div className="grid gap-6" data-testid="fleet-equipment-list">
          {filteredEquipment.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">🚚</div>
              <p>No {filterType.toLowerCase()} equipment found.</p>
              <p className="text-sm">Click "Add Equipment" to get started.</p>
            </div>
          )}

          {filteredEquipment.map((item) => (
            <div key={item.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200" data-testid={`fleet-equipment-card-${item.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="text-2xl sm:text-3xl">{getEquipmentIcon(item.equipment_type)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 break-words" data-testid={`fleet-equipment-title-${item.id}`}>
                          {item.unit_number} - {item.year} {item.make} {item.model}
                        </h3>
                        <p className="text-gray-600 text-sm">{item.equipment_type}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2" data-testid={`fleet-equipment-status-${item.id}`}>
                      {item.repairs_needed && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">
                          🔧 REPAIRS
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(item.status).replace('text-', 'bg-').replace('-400', '-100')} ${getStatusColor(item.status).replace('-400', '-800')}`}>
                        {item.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-gray-600" data-testid={`fleet-equipment-details-${item.id}`}>
                    <p><span className="text-gray-500 font-medium">VIN:</span> {item.vin}</p>
                    {item.license_plate && <p><span className="text-gray-500 font-medium">Plate:</span> {item.license_plate}</p>}
                    {item.next_cvor && (
                      <p><span className="text-gray-500 font-medium">Safety Inspection Due:</span> 
                        <span className={getCVORStatusColor(item.next_cvor).replace('text-', 'text-')}> {new Date(item.next_cvor).toLocaleDateString()}</span>
                      </p>
                    )}
                    {item.description && <p><span className="text-gray-500 font-medium">Description:</span> {item.description}</p>}
                    {item.notes && <p><span className="text-gray-500 font-medium">Notes:</span> {item.notes}</p>}
                    <p><span className="text-gray-500 font-medium">Added:</span> {new Date(item.created_date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 w-full lg:w-auto" data-testid={`fleet-equipment-actions-${item.id}`}>
                  <button
                    onClick={() => openFleetFiles(item)}
                    className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
                    data-testid={`fleet-files-button-${item.id}`}
                  >
                    📁 Files
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
                    data-testid={`fleet-edit-button-${item.id}`}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
                    data-testid={`fleet-delete-button-${item.id}`}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Fleet Files Modal */}
        {showFleetFiles && fleetFilesEquipment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="fleet-files-modal">
            <div className="bg-white rounded-xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">📁 Fleet Files</h2>
                  <p className="text-sm text-gray-600 mt-1">{fleetFilesEquipment.unit_number} — {fleetFilesEquipment.year} {fleetFilesEquipment.make} {fleetFilesEquipment.model}</p>
                </div>
                <button
                  onClick={() => {
                    setShowFleetFiles(false);
                    setFleetFilesEquipment(null);
                    setFleetFiles([]);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                  data-testid="fleet-files-close"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-3">
                {fleetFiles.length === 0 ? (
                  <div className="text-sm text-gray-500">No files uploaded yet.</div>
                ) : (
                  fleetFiles.map((doc) => (
                    <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-gray-200 rounded-lg p-3">
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">{doc.filename}</div>
                        <div className="text-xs text-gray-500">Uploaded by {doc.uploaded_by} · {new Date(doc.upload_date).toLocaleDateString()}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => viewFleetFile(doc)}
                          className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-semibold"
                          data-testid={`fleet-file-view-${doc.id}`}
                        >
                          View
                        </button>
                        <button
                          onClick={() => downloadFleetFile(doc)}
                          className="px-3 py-1.5 rounded-md bg-green-600 text-white text-xs font-semibold"
                          data-testid={`fleet-file-download-${doc.id}`}
                        >
                          Download
                        </button>
                        {currentUser?.rawRole === 'admin' && (
                          <button
                            onClick={() => handleFleetFileDelete(doc.id)}
                            className="px-3 py-1.5 rounded-md border border-red-200 text-red-700 text-xs font-semibold"
                            data-testid={`fleet-file-delete-${doc.id}`}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {currentUser?.rawRole === 'admin' && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <div className="text-sm font-semibold text-gray-800 mb-3">Upload PDF/DOC</div>
                  <form onSubmit={handleFleetFileUpload} className="space-y-3">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFleetFileSelect}
                      className="block w-full text-xs"
                      data-testid="fleet-file-input"
                    />
                    <input
                      type="text"
                      value={fleetFileForm.uploaded_by}
                      onChange={(e) => setFleetFileForm(prev => ({ ...prev, uploaded_by: e.target.value }))}
                      placeholder="Uploaded by"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      data-testid="fleet-file-uploaded-by"
                      required
                    />
                    <button
                      type="submit"
                      disabled={fleetFilesUploading || !fleetFileForm.file_data}
                      className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-emerald-300"
                      data-testid="fleet-file-upload-button"
                    >
                      {fleetFilesUploading ? 'Uploading…' : 'Upload File'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fleet Summary */}
        {equipment.length > 0 && (
          <div className="mt-8 space-y-6">
            
            {/* Fleet Type Summary */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Fleet Summary</h3>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">🚛</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {equipment.filter(item => item.equipment_type === 'Truck').length}
                  </div>
                  <div className="text-sm text-gray-600">Trucks</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">🚚</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {equipment.filter(item => item.equipment_type === 'Trailer').length}
                  </div>
                  <div className="text-sm text-gray-600">Trailers</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">🚜</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {equipment.filter(item => item.equipment_type === 'Equipment').length}
                  </div>
                  <div className="text-sm text-gray-600">Equipment</div>
                </div>
              </div>
            </div>

            {/* CVOR & Maintenance Status */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">🚛 CVOR & Maintenance Status</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">⚠️</div>
                  <div className="text-2xl font-bold text-red-600">
                    {equipment.filter(item => {
                      if (!item.next_cvor) return false;
                      const today = new Date();
                      const cvorDate = new Date(item.next_cvor);
                      return cvorDate < today;
                    }).length}
                  </div>
                  <div className="text-sm text-red-600">CVOR Expired</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">📅</div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {equipment.filter(item => {
                      if (!item.next_cvor) return false;
                      const today = new Date();
                      const cvorDate = new Date(item.next_cvor);
                      const daysUntilDue = Math.ceil((cvorDate - today) / (1000 * 60 * 60 * 24));
                      return daysUntilDue >= 0 && daysUntilDue <= 30;
                    }).length}
                  </div>
                  <div className="text-sm text-yellow-600">CVOR Due Soon</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">🔧</div>
                  <div className="text-2xl font-bold text-red-600">
                    {equipment.filter(item => item.repairs_needed).length}
                  </div>
                  <div className="text-sm text-red-600">Need Repairs</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl mb-2">✅</div>
                  <div className="text-2xl font-bold text-green-600">
                    {equipment.filter(item => {
                      const repairOK = !item.repairs_needed;
                      const cvorOK = !item.next_cvor || (() => {
                        const today = new Date();
                        const cvorDate = new Date(item.next_cvor);
                        const daysUntilDue = Math.ceil((cvorDate - today) / (1000 * 60 * 60 * 24));
                        return daysUntilDue > 30;
                      })();
                      return repairOK && cvorOK;
                    }).length}
                  </div>
                  <div className="text-sm text-green-600">Good Condition</div>
                </div>
              </div>
            </div>
            
          </div>
        )}

        </div>
      </div>
  );
};

export default FleetModule;
